import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon, Button } from '@popety-io/ui'
import { Valuation } from '@popety-io/types'
import { getProviderInfo } from '../../utils/valuationColors'
import {
  DetailContainer,
  BackButtonContainer,
  DetailHeader,
  DetailMainInfo,
  DetailTitle,
  DetailMeta,
  MetaItem,
  Divider,
  ProviderBadge,
  DetailValues,
  ValueRow,
  ValueLabel,
  ValueAmount,
  ValueRange,
  DetailDescription,
  SectionTitle,
  InfoGrid,
  InfoItem,
  InfoLabel,
  InfoValue,
  ConfidenceSection,
  ConfidenceDots,
  ConfidenceDot,
  ConfidenceText,
} from './AutoValuationDetail.styles'

interface AutoValuationDetailProps {
  valuation: Valuation
  onBack: () => void
}

export const AutoValuationDetail: FC<AutoValuationDetailProps> = ({
  valuation,
  onBack,
}) => {
  const { t } = useTranslation('estimate')

  const { name: providerName, color: providerColor } = useMemo(
    () => getProviderInfo(valuation.source),
    [valuation.source]
  )

  const formatCurrency = (value: number) =>
    `CHF ${Math.round(value).toLocaleString('de-CH')}`

  const formatDate = (date: Date) => {
    const now = new Date()
    const diffInDays = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
    )

    if (diffInDays === 0) return t('valuations.detail.today')
    if (diffInDays === 1) return t('valuations.detail.yesterday')
    if (diffInDays < 7)
      return t('valuations.detail.daysAgo', { days: diffInDays })
    if (diffInDays < 30)
      return t('valuations.detail.weeksAgo', {
        weeks: Math.floor(diffInDays / 7),
      })

    return date.toLocaleDateString('de-CH')
  }

  const getConfidenceLabel = (confidence: number) => {
    switch (confidence) {
      case 3:
        return t('valuations.confidence.high')
      case 2:
        return t('valuations.confidence.medium')
      case 1:
        return t('valuations.confidence.low')
      default:
        return ''
    }
  }

  // Normalize key to snake_case for consistent lookup
  const normalizeKey = (key: string): string => {
    return key
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/([a-z])([A-Z])/g, '$1_$2')
      .toLowerCase()
  }

  // Map API meta keys to translation keys
  const getMetaLabelKey = (key: string): string => {
    const normalizedKey = normalizeKey(key)
    const keyMap: Record<string, string> = {
      provider: 'autoValuations.meta.provider',
      method: 'autoValuations.meta.method',
      model_version: 'autoValuations.meta.modelVersion',
      deal_type: 'autoValuations.meta.dealType',
      price_per_m2: 'autoValuations.meta.pricePerM2',
      'price_per_m²': 'autoValuations.meta.pricePerM2',
      confidence_score: 'autoValuations.meta.confidenceScore',
      listings_count: 'autoValuations.meta.listingsCount',
      data_range: 'autoValuations.meta.dataRange',
      // These are per land m², never per living m² — see the comment in
      // parseWuestPartnerValuation. Keep them distinct from a bare "Value per
      // m²" so a future provider can't collide with the land-only figure.
      'value_per_m²_of_land_(conservative)':
        'autoValuations.meta.valuePerM2LandConservative',
      'value_per_m²_of_land_(optimistic)':
        'autoValuations.meta.valuePerM2LandOptimistic',
      land_area: 'autoValuations.meta.landArea',
    }
    return keyMap[normalizedKey] || ''
  }

  // Translate common meta values
  const translateMetaValue = (value: string): string => {
    const valueTranslations: Record<string, string> = {
      unknown: t('autoValuations.meta.unknown'),
      purchase: t('autoValuations.meta.values.purchase'),
      sale: t('autoValuations.meta.values.sale'),
      rent: t('autoValuations.meta.values.rent'),
    }
    const lowerValue = value.toLowerCase()
    return valueTranslations[lowerValue] || value
  }

  // Format meta value, handling special cases
  const formatMetaValue = (key: string, value: unknown): string => {
    if (value === null || value === undefined || value === '') {
      return t('autoValuations.meta.unknown')
    }
    if (typeof value === 'number') {
      return value.toLocaleString('de-CH')
    }
    const strValue = String(value)
    return translateMetaValue(strValue)
  }

  // Parse metadata to display
  const metaEntries = useMemo(() => {
    if (!valuation.meta || Object.keys(valuation.meta).length === 0) return []

    return Object.entries(valuation.meta)
      .filter(([_key, value]) => value !== null && value !== undefined)
      .map(([key, value]) => {
        const labelKey = getMetaLabelKey(key)
        return {
          label: labelKey
            ? t(labelKey)
            : key
                .replace(/_/g, ' ')
                .replace(/([A-Z])/g, ' $1')
                .trim(),
          value: formatMetaValue(key, value),
        }
      })
  }, [valuation.meta, t])

  return (
    <DetailContainer>
      <BackButtonContainer>
        <Button variant="ghost" size="small" onClick={onBack}>
          <Icon name="arrow-left" size="small" />
          {t('autoValuations.back')}
        </Button>
      </BackButtonContainer>

      <DetailHeader>
        <DetailMainInfo>
          <DetailTitle>{valuation.name}</DetailTitle>
          <DetailMeta>
            <ProviderBadge $color={providerColor}>{providerName}</ProviderBadge>
            <Divider />
            <MetaItem>
              <Icon name="clock" size="small" />
              {formatDate(valuation.updatedAt)}
            </MetaItem>
          </DetailMeta>
        </DetailMainInfo>
      </DetailHeader>

      <DetailValues>
        <ValueRow>
          <ValueLabel>{t('valuations.detail.estimatedValue')}</ValueLabel>
          <ValueAmount $color={providerColor}>
            {formatCurrency(valuation.value)}
          </ValueAmount>
        </ValueRow>
        {valuation.range && (
          <ValueRow>
            <ValueLabel>{t('valuations.detail.valueRange')}</ValueLabel>
            <ValueRange>
              {formatCurrency(valuation.range.min)} -{' '}
              {formatCurrency(valuation.range.max)}
            </ValueRange>
          </ValueRow>
        )}
      </DetailValues>

      {valuation.confidence && (
        <ConfidenceSection>
          <SectionTitle>{t('valuations.detail.confidence')}</SectionTitle>
          <ConfidenceDots>
            <ConfidenceDot
              $filled={valuation.confidence >= 1}
              $color={providerColor}
            />
            <ConfidenceDot
              $filled={valuation.confidence >= 2}
              $color={providerColor}
            />
            <ConfidenceDot
              $filled={valuation.confidence >= 3}
              $color={providerColor}
            />
            <ConfidenceText>
              {getConfidenceLabel(valuation.confidence)}
            </ConfidenceText>
          </ConfidenceDots>
        </ConfidenceSection>
      )}

      {(valuation.description || valuation.source) && (
        <>
          <SectionTitle>{t('autoValuations.methodology')}</SectionTitle>
          <DetailDescription>
            {t(`autoValuations.methodDescriptions.${valuation.source}`, {
              defaultValue: valuation.description || '',
            })}
          </DetailDescription>
        </>
      )}

      {metaEntries.length > 0 && (
        <>
          <SectionTitle>{t('autoValuations.details')}</SectionTitle>
          <InfoGrid>
            {metaEntries.map((item, index) => (
              <InfoItem key={index}>
                <InfoLabel>{item.label}</InfoLabel>
                <InfoValue>{item.value}</InfoValue>
              </InfoItem>
            ))}
          </InfoGrid>
        </>
      )}
    </DetailContainer>
  )
}
